import type { WorkspaceOperationInOut } from '@festina/configuration-general-types/configuration'
import {
  LayerKey,
  OperationLayerSet,
  useFetchWorkspaceOperations,
  type WorkspaceKey,
} from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useConfigurationComponentAvailability } from '../../hooks/useConfigurationComponentAvailability'

export const useWorkspaceOperations = <T extends WorkspaceOperationInOut>(
  keyValue: Optional<WorkspaceKey>,
  guard?: (operation: WorkspaceOperationInOut) => operation is T,
): {
  data: T[]
  allLayers: LayerKey[]
  allBlocks: string[]
  filteredLayers: LayerKey[]
  filteredBlocks: string[]
  isFetching: boolean
  /** Whether the operations are being loaded for the first time, with nothing to show yet. */
  isLoading: boolean
} => {
  const isConfigurationComponentAvailable =
    useConfigurationComponentAvailability()
  const {
    data: configComponentOperations,
    isFetching,
    isLoading,
  } = useFetchWorkspaceOperations({
    server: keyValue?.branchKey.server,
    workspaceName: keyValue?.name,
    enabled: isConfigurationComponentAvailable,
  })
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  return useMemo(() => {
    const allOperations = configComponentOperations ?? []
    const filteredOperations = guard
      ? allOperations.filter(guard)
      : (allOperations as T[])
    const allLayers = Array.from(
      new OperationLayerSet(
        allOperations.map(
          (operation) =>
            new LayerKey({
              tenantId: operation.tenantId,
              layerId: operation.layer,
            }),
        ),
      ).values(),
    )

    const allBlocks = Array.from(
      new Set(
        allOperations.map((operation) => operation.block ?? rootBlock),
      ).values(),
    )
    const filteredLayers = Array.from(
      new OperationLayerSet(
        guard
          ? filteredOperations.map(
              (operation) =>
                new LayerKey({
                  tenantId: operation.tenantId,
                  layerId: operation.layer,
                }),
            )
          : allLayers,
      ).values(),
    )

    const filteredBlocks = Array.from(
      new Set(
        guard
          ? filteredOperations.map((operation) => operation.block ?? rootBlock)
          : allBlocks,
      ).values(),
    )

    return {
      data: filteredOperations,
      allLayers,
      allBlocks,
      filteredLayers,
      filteredBlocks,
      isFetching,
      isLoading,
    }
  }, [configComponentOperations, guard, isFetching, isLoading, rootBlock])
}
